import { AngleLeftLightIcon } from '@mm/icons';
import { ReactComponent as TrashIcon } from 'assets/garbage.svg';
import { ReactComponent as EditIcon } from 'assets/messenger-ads-icons/edit.svg';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Popover from 'react-tiny-popover';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import TextInput from '../../../../../common/components/TextInput';
import { NeedHelpLabel } from '../../../common/components/NeedHelpLabel/NeedHelpLabel';
import { DEFAULT_NAME } from '../../constants/constants';
import './EditHeader.pcss';

type Props = {
  valid: boolean;
  entity: { name: string } | null;
  defaultName: string;
  showSave?: boolean;
  errors?: string[];
  onSaveName?: (name: string) => void;
  onClickDelete: () => void;
  saveButtonClass: string;
  saveButtonText: string;
  onSaveClick: () => void;
  saveDisabled: boolean;
  showOnlyActionButton?: boolean;
};

export const EditHeader = (props: Props) => {
  const {
    defaultName,
    showSave,
    entity,
    valid,
    onSaveName,
    errors,
    onClickDelete,
    saveButtonClass,
    saveButtonText,
    saveDisabled,
    onSaveClick,
    showOnlyActionButton,
  } = props;
  const [nameEditMode, setNameEditMode] = useState(false);
  const [localName, setLocalName] = useState(
    entity ? entity.name : defaultName,
  );
  const history = useHistory();
  const isMessageAd = defaultName === DEFAULT_NAME;
  const link = isMessageAd
    ? 'https://mobilemonkey.com/help/article/0nn4iudlec-launch-your-first-click-to-messenger-ad'
    : 'https://mobilemonkey.com/help/article/i7nobsrgdd-creating-a-sponsored-message';

  const containerClasses = classnames('edit-header', {
    'edit-header--invalid': !valid,
  });

  const classList = classnames('edit-header__name-input', {
    'edit-header--name-disabled-mode': !nameEditMode,
  });

  const saveButtonClassList = classnames(
    'edit-header__name-save-button',
    'edit-header__button',
    saveButtonClass,
    {
      'edit-header--save-disabled': !localName,
    },
  );

  useEffect(() => {
    setLocalName(entity ? entity.name : defaultName);
  }, [entity]);

  const onClickCancelSaveName = () => {
    if (!entity) {
      return;
    }
    setLocalName(entity.name);
    setNameEditMode(false);
  };

  const onClickSaveName = () => {
    if (onSaveName) {
      onSaveName(localName);
    }

    setNameEditMode(false);
  };

  const [errorPopoverIsOpen, setErrorPopoverIsOpen] = useState(false);

  return !showOnlyActionButton ? (
    <div className={containerClasses}>
      <div className="edit-header__title-area-container">
        <div
          className="edit-header__back-button"
          onClick={() => history.goBack()}
        >
          <AngleLeftLightIcon />
          Back
        </div>
        <div className="edit-header__title-area">
          <div className="edit-header__title-area-left">
            {nameEditMode ? (
              <TextInput
                value={localName}
                onChange={e => setLocalName(e.target.value)}
                disabled={!nameEditMode}
                className={classList}
                maxLength={50}
              />
            ) : (
              <div className="edit-header__name-preview">{localName}</div>
            )}

            {!nameEditMode && (
              <div className="edit-header__editing-container">
                <EditIcon
                  onClick={() => setNameEditMode(true)}
                  className="edit-header__edit-icon"
                />
                {errors && (
                  <Popover
                    containerClassName="edit-header__validation-tooltip-container"
                    content={
                      <div className="edit-header__validation-tooltip-content">
                        <ul className="edit-header__validation-messages">
                          {errors.map((message, index) => (
                            <li
                              className="edit-header__validation-message"
                              key={index}
                            >
                              {message}
                            </li>
                          ))}
                        </ul>
                      </div>
                    }
                    position={['bottom', 'right']}
                    isOpen={errorPopoverIsOpen}
                  >
                    <div
                      className="edit-header__validation-tooltip-anchor"
                      onMouseOver={() => setErrorPopoverIsOpen(true)}
                      onMouseLeave={() => setErrorPopoverIsOpen(false)}
                    >
                      <i className="fas fa-exclamation-circle" />
                    </div>
                  </Popover>
                )}
              </div>
            )}

            {nameEditMode && (
              <>
                <PrimaryButton
                  color={ButtonColor.invertedRed}
                  onClick={onClickCancelSaveName}
                  className="edit-header__name-cancel-button edit-header__button"
                >
                  Cancel
                </PrimaryButton>

                <PrimaryButton
                  onClick={onClickSaveName}
                  className={saveButtonClassList}
                >
                  Save
                </PrimaryButton>
              </>
            )}
          </div>

          <div className="edit-header__title-area-right">
            <NeedHelpLabel link={link} />
            <div
              className="edit-header__delete-wrapper"
              onClick={onClickDelete}
            >
              <TrashIcon className="edit-header__delete-icon" />
              <span>Delete</span>
            </div>
            {(showSave === undefined ? true : showSave) && (
              <PrimaryButton
                className={classnames('edit-header__button', saveButtonClass)}
                onClick={onSaveClick}
                disabled={saveDisabled}
                color={ButtonColor.blue}
              >
                {saveButtonText}
              </PrimaryButton>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div>
      {(showSave === undefined ? true : showSave) && (
        <PrimaryButton
          className={classnames('edit-header__button', saveButtonClass)}
          onClick={onSaveClick}
          disabled={saveDisabled}
          color={ButtonColor.blue}
        >
          {saveButtonText}
        </PrimaryButton>
      )}
    </div>
  );
};
